import { halfHour } from 'shared/constants';
import { DataType } from 'shared/types/data-type';
import { Task } from 'shared/types/task';
import { WeekDays } from 'shared/types/week-days';
import { getDueTaskPlanning } from 'shared/utils/get-task-planning';

import { ExtendedProps } from './event-allow';

export type BuildDraggableEventDataOptions = {
  tasks: Task[];
  weekStartsOn?: WeekDays;
  includeDuration?: boolean;
  includeDueDateAsOriginDate?: boolean;
};

// Single payload builder for every external-drag producer feeding the
// calendar's eventReceive — task drops resolve from extendedProps.task, so a
// producer that omits it silently breaks drag-to-schedule.
export const buildDraggableEventData = (
  el: HTMLElement,
  {
    tasks,
    weekStartsOn,
    includeDuration,
    includeDueDateAsOriginDate,
  }: BuildDraggableEventDataOptions,
) => {
  const dataEl = el.matches('[data-component-type]')
    ? el
    : el.querySelector('[data-component-type]');
  const id = dataEl?.getAttribute('data-id');

  if (!dataEl || !id) {
    return;
  }

  const task = tasks.find((task) => task.id === id);
  const title = dataEl.getAttribute('data-title');
  const componentType = dataEl.getAttribute('data-component-type') as DataType;

  const extendedProps: ExtendedProps = { weekStartsOn, task };

  switch (componentType) {
    case DataType.Task:
      extendedProps.taskId = id;
      break;
    case DataType.Goal:
      extendedProps.goalId = id;
      break;
    case DataType.LifeArea:
      extendedProps.lifeAreaId = id;
      break;
  }

  // for allowing dragging of the task from the list onto a due date, we get the due task planning as origin date.
  if (includeDueDateAsOriginDate && task && weekStartsOn) {
    const duePlanning = getDueTaskPlanning(task, {
      date: new Date(),
      today: new Date(),
      weekStartsOn,
      uncompletedOnly: true,
    });
    extendedProps.originDate = duePlanning[duePlanning.length - 1]?.date;
  }

  const eventBase = {
    id,
    title,
    extendedProps,
    create: true,
  };

  if (!includeDuration) {
    return eventBase;
  }

  return {
    ...eventBase,
    duration: { ms: dataEl.getAttribute('data-duration') ?? halfHour },
  };
};
